import { AtSign, Heart, MessageCircle, Quote, Repeat2, UserPlus } from 'lucide-react'
import type { ComponentType } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import type { Notification, NotificationType } from '@/api/types'
import { Avatar } from '@/components/ui/Avatar'
import { useT } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { cn } from '@/lib/cn'
import { useFormat } from '@/lib/format'

const META: Record<
  NotificationType,
  { icon: ComponentType<{ className?: string }>; color: string; text: MessageKey }
> = {
  follow: { icon: UserPlus, color: 'text-brand-ink', text: 'notif.follow' },
  like: { icon: Heart, color: 'text-like', text: 'notif.like' },
  repost: { icon: Repeat2, color: 'text-repost', text: 'notif.repost' },
  reply: { icon: MessageCircle, color: 'text-brand-ink', text: 'notif.reply' },
  quote: { icon: Quote, color: 'text-brand-ink', text: 'notif.quote' },
  mention: { icon: AtSign, color: 'text-brand-ink', text: 'notif.mention' },
}

interface Props {
  notification: Notification
  /**
   * 是否高亮成「新的」。默认跟随 `read`，但通知页会覆盖它：
   * 打开页面就标记已读，只跟随 read 的话标记会在几百毫秒内消失，等于没有。
   */
  highlight?: boolean
}

/**
 * 一条通知。未读用左侧色条标出而不是整行染色 ——
 * 整行底色会和「已选中」混淆。
 */
export function NotificationItem({ notification, highlight }: Props) {
  const { relativeTime } = useFormat()
  const t = useT()
  const isNew = highlight ?? !notification.read
  const navigate = useNavigate()
  const { icon: Icon, color, text } = META[notification.type]
  const actor = notification.actor
  const post = notification.post

  // 回复 / 引用 / 提及指向对方那条帖子；点赞 / 转发指向我自己被赞的那条
  const target = post
    ? `/${post.author.username}/status/${post.id}`
    : actor
      ? `/${actor.username}`
      : null

  return (
    <article
      onClick={() => target && navigate(target)}
      className={cn(
        'relative flex cursor-pointer gap-3 border-b border-line px-4 py-3 transition-colors',
        'hover:bg-surface-hover',
      )}
    >
      {isNew && (
        <span
          className="absolute left-0 top-0 h-full w-1 bg-brand-ink"
          aria-label={t('notif.unread')}
        />
      )}

      <Icon className={cn('mt-1 size-5 shrink-0', color)} aria-hidden />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {actor && (
            <Avatar
              src={actor.avatar_url}
              alt={actor.display_name}
              to={`/${actor.username}`}
              size="xs"
            />
          )}
          <p className="min-w-0 flex-1 text-[15px]">
            {actor ? (
              <Link
                to={`/${actor.username}`}
                onClick={(event) => event.stopPropagation()}
                className="font-bold hover:underline"
              >
                {actor.display_name}
              </Link>
            ) : (
              <span className="font-bold text-ink-muted">{t('notif.deletedUser')}</span>
            )}{' '}
            <span className="text-ink-muted">{t(text)}</span>
          </p>
          <time className="shrink-0 text-[13px] text-ink-muted">
            {relativeTime(notification.created_at)}
          </time>
        </div>

        {post?.content && (
          <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-[15px] text-ink-muted">
            {post.content}
          </p>
        )}
      </div>
    </article>
  )
}
